import { useCallback, useEffect, useState } from 'react'
import {
  getVapidKey,
  registerWebPush,
  unregisterWebPush,
} from '@/lib/api/features/webPush/api'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

const SW_URL = '/sw-push.js'

type Status =
  | 'unsupported'      // browser lacks Notification / PushManager / serviceWorker
  | 'not-configured'   // backend has no VAPID keys
  | 'permission-denied'// user blocked notifications at OS/browser level
  | 'idle'             // ready but not subscribed
  | 'subscribed'       // browser is subscribed and registered with backend

interface UseWebPushRegistrationResult {
  status: Status
  isSubscribed: boolean
  /**
   * Idempotent: prompts permission if needed, subscribes the browser, and posts
   * the subscription to the backend. Safe to call repeatedly — re-registers the
   * same endpoint and reactivates the row server-side.
   */
  enable: () => Promise<boolean>
  /**
   * Hard-disconnect this browser: unsubscribes from the push service AND tells
   * the backend to delete the row. Per-event Push toggles do NOT call this —
   * subscription is retained across all events and workspaces.
   */
  disable: () => Promise<void>
}

/**
 * Browser web-push subscription lifecycle. The subscription is **user-scoped**
 * (one set of devices across all workspaces), so this hook only deals with the
 * "is this browser registered?" question — per-event Push preferences are
 * handled by the notification settings UI.
 */
export function useWebPushRegistration(): UseWebPushRegistrationResult {
  const [status, setStatus] = useState<Status>('idle')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [vapidKey, setVapidKey] = useState<string | null>(null)

  // Browser capability + backend configuration check on mount.
  useEffect(() => {
    let cancelled = false

    const supported =
      typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window

    if (!supported) {
      setStatus('unsupported')
      return
    }

    getVapidKey()
      .then(async (resp) => {
        if (cancelled) return
        if (!resp.isConfigured || !resp.vapidPublicKey) {
          setStatus('not-configured')
          return
        }
        setVapidKey(resp.vapidPublicKey)

        if (Notification.permission === 'denied') {
          setStatus('permission-denied')
          return
        }

        // Detect existing subscription so the UI shows correct state on reload.
        try {
          const reg = await navigator.serviceWorker.getRegistration(SW_URL)
          const sub = await reg?.pushManager.getSubscription()
          if (!cancelled) {
            setIsSubscribed(Boolean(sub))
            setStatus(sub ? 'subscribed' : 'idle')
          }
        } catch (err) {
          if (!cancelled) setStatus('idle')
        }
      })
      .catch((err) => {
        if (cancelled) return
        logger.logError(err as Error, LogCategory.API_ERROR, {
          component: 'useWebPushRegistration',
          action: 'fetch_vapid_key',
        })
        setStatus('not-configured')
      })

    return () => {
      cancelled = true
    }
  }, [])

  const enable = useCallback(async (): Promise<boolean> => {
    if (status === 'unsupported' || status === 'not-configured') return false
    if (!vapidKey) return false

    try {
      // Permission flow: request -> denied is terminal, granted continues.
      if (Notification.permission !== 'granted') {
        const result = await Notification.requestPermission()
        if (result !== 'granted') {
          setStatus(result === 'denied' ? 'permission-denied' : 'idle')
          return false
        }
      }

      const registration =
        (await navigator.serviceWorker.getRegistration(SW_URL))
        ?? (await navigator.serviceWorker.register(SW_URL))

      // Wait for the SW to be active before subscribing.
      if (registration.installing || registration.waiting) {
        await new Promise<void>((resolve) => {
          const target = registration.installing ?? registration.waiting
          if (!target) {
            resolve()
            return
          }
          target.addEventListener('statechange', () => {
            if (target.state === 'activated') resolve()
          })
        })
      }

      // applicationServerKey must be the VAPID public key as a Uint8Array.
      const applicationServerKey = urlBase64ToUint8Array(vapidKey)

      let subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        })
      }

      const json = subscription.toJSON() as PushSubscriptionJSON & {
        keys?: { p256dh?: string; auth?: string }
      }
      const p256dh = json.keys?.p256dh
      const auth = json.keys?.auth
      if (!subscription.endpoint || !p256dh || !auth) {
        throw new Error('PushSubscription missing endpoint or keys')
      }

      await registerWebPush({
        endpoint: subscription.endpoint,
        p256dh,
        auth,
        userAgent:
          typeof navigator !== 'undefined' && navigator.userAgent
            ? navigator.userAgent.slice(0, 500)
            : null,
      })

      setIsSubscribed(true)
      setStatus('subscribed')
      return true
    } catch (err) {
      logger.logError(err as Error, LogCategory.API_ERROR, {
        component: 'useWebPushRegistration',
        action: 'enable',
      })
      return false
    }
  }, [status, vapidKey])

  const disable = useCallback(async (): Promise<void> => {
    try {
      const registration = await navigator.serviceWorker.getRegistration(SW_URL)
      const subscription = await registration?.pushManager.getSubscription()
      if (subscription) {
        const endpoint = subscription.endpoint
        await subscription.unsubscribe()
        try {
          await unregisterWebPush({ endpoint })
        } catch (err) {
          logger.logError(err as Error, LogCategory.API_ERROR, {
            component: 'useWebPushRegistration',
            action: 'unregister_backend',
          })
        }
      }
      setIsSubscribed(false)
      setStatus('idle')
    } catch (err) {
      logger.logError(err as Error, LogCategory.API_ERROR, {
        component: 'useWebPushRegistration',
        action: 'disable',
      })
    }
  }, [])

  return { status, isSubscribed, enable, disable }
}

/**
 * VAPID public keys are sent as base64url-encoded byte strings; the Push API
 * requires an ArrayBuffer-backed view. Standard helper from the W3C examples,
 * returning ArrayBuffer (not Uint8Array<ArrayBufferLike>) to satisfy TS strict
 * mode against PushSubscriptionOptionsInit.applicationServerKey.
 */
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const buffer = new ArrayBuffer(rawData.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < rawData.length; i += 1) {
    view[i] = rawData.charCodeAt(i)
  }
  return buffer
}
